import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatDate, stripHtml } from '../utils';
import Modal from './Modal';
import Icon from './Icon';

// ─── NEWSPAPER TIMELINE ────────────────────────────────────────────────────────
//
//  Props:
//    events       {array}    event objects — each may have .featured, .breakingNews,
//                            .pullQuote (string override) fields
//    colors       {object}   theme colors + layout options stored as extra keys:
//                              npColumnCount (2|3|4, default 3)
//                              npSepia (bool)        npScanLines (bool)
//                              npPullQuotes (bool, default true)
//                              npEditionHeader (bool) npTagline (string)
//                              npEditionNumber (string) npBreakingNewsTicker (bool)
//                              npGrayscaleDefault (bool)
//    onEventClick {function}
//    timeline     {object}   full timeline (title, id, privacy, description, etc.)
//    isEmbed      {boolean}
//    canEdit      {boolean}
//

const MASONRY_GAP = 20;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const safeSlug = (title) =>
  (title || 'newspaper').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'newspaper';

function extractPullQuote(ev) {
  const text = stripHtml(ev.description || '').trim();
  if (!text || text.length <= 150) return null;
  if (ev.pullQuote) return ev.pullQuote.slice(0, 200);
  const m = text.match(/^(.+?[.!?])(?:\s|$)/);
  if (m && m[1] && m[1].length <= 200) return m[1];
  return text.slice(0, 100) + '…';
}

// Masonry layout: places items (cards, featured cards, full-width headers)
// using a shortest-column algorithm.
function calculateMasonryLayout(items, itemEls, colCount, gap, containerWidth) {
  if (colCount <= 0 || containerWidth <= 0) return { positions: [], containerHeight: 0 };
  const colW = Math.max(1, (containerWidth - gap * (colCount - 1)) / colCount);
  const colH = Array(colCount).fill(0);
  const positions = [];

  items.forEach((item, i) => {
    const el = itemEls[i];
    if (!el) { positions.push(null); return; }
    const h = el.offsetHeight || 0;

    if (item.type === 'header') {
      // Full-width — align to tallest column, then reset all
      const top = Math.max(...colH);
      positions.push({ top, left: 0, width: containerWidth });
      colH.fill(top + h + gap);
    } else if ((item.ev?.featured || item.ev?.breakingNews) && colCount >= 2) {
      // Span 2 adjacent columns at the best (shallowest) pair
      let bestCol = 0;
      let bestMax = Infinity;
      for (let c = 0; c <= colCount - 2; c++) {
        const m = Math.max(colH[c], colH[c + 1]);
        if (m < bestMax) { bestMax = m; bestCol = c; }
      }
      const top  = Math.max(colH[bestCol], colH[bestCol + 1]);
      const left = bestCol * (colW + gap);
      const width = colCount === 2 ? containerWidth : (2 * colW + gap);
      positions.push({ top, left, width });
      colH[bestCol] = top + h + gap;
      colH[bestCol + 1] = top + h + gap;
    } else {
      // Regular — shortest column
      const col  = colH.indexOf(Math.min(...colH));
      const top  = colH[col];
      const left = col * (colW + gap);
      positions.push({ top, left, width: colW });
      colH[col] += h + gap;
    }
  });

  const maxH = Math.max(...colH);
  return { positions, containerHeight: maxH > gap ? maxH - gap : 0 };
}

// ─── Component ────────────────────────────────────────────────────────────────

const NewspaperTimeline = ({
  events = [],
  colors = {},
  onEventClick,
  manualOrder = false,
  timeline = null,
  isEmbed = false,
  canEdit = false,
}) => {

  // ── Layout options from colors object ──────────────────────────────────────
  const npColumnCount    = colors?.npColumnCount    ?? 3;
  const npSepia          = !!colors?.npSepia;
  const npScanLines      = !!colors?.npScanLines;
  const npPullQuotes     = colors?.npPullQuotes     !== false;
  const npEditionHeader  = !!colors?.npEditionHeader;
  const npTagline        = colors?.npTagline        ?? '';
  const npEditionNumber  = colors?.npEditionNumber  ?? '';
  const npBreakingTicker = colors?.npBreakingNewsTicker !== false;
  const npGrayscaleDef   = !!colors?.npGrayscaleDefault;

  // ── Color values ───────────────────────────────────────────────────────────
  const primary   = colors.primary   || '#1a1a1a';
  const secondary = colors.secondary || '#444';
  const tertiary  = colors.tertiary  || '#f9f6f0';
  const accent    = colors.accent    || primary;

  // ── State ──────────────────────────────────────────────────────────────────
  const [sort,          setSort]          = useState('chrono');
  const [activeChapter, setActiveChapter] = useState(null);
  const [grayscale,     setGrayscale]     = useState(npGrayscaleDef && npSepia);
  const [positions,     setPositions]     = useState([]);
  const [containerH,    setContainerH]    = useState(0);
  const [animated,      setAnimated]      = useState(false);
  const [isExporting,   setIsExporting]   = useState(false);
  const [exportMsg,     setExportMsg]     = useState('');
  const [embedOpen,     setEmbedOpen]     = useState(false);
  const [embedCopied,   setEmbedCopied]   = useState(false);
  const [embedWidth,    setEmbedWidth]    = useState('100%');
  const [embedHeight,   setEmbedHeight]   = useState('700');
  const [embedResp,     setEmbedResp]     = useState(false);
  const [windowWidth,   setWindowWidth]   = useState(
    () => typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  // ── Refs ───────────────────────────────────────────────────────────────────
  const rootRef      = useRef(null);
  const containerRef = useRef(null);
  const itemRefs     = useRef([]);
  const debounceRef  = useRef(null);
  const layoutDone   = useRef(false);

  // ── Responsive column count ────────────────────────────────────────────────
  const effectiveCols = useMemo(() => {
    if (windowWidth < 768)  return 1;
    if (windowWidth < 1024) {
      if (npColumnCount >= 4) return 3;
      if (npColumnCount >= 3) return 2;
      return npColumnCount; // 2-col stays at 2 on tablet
    }
    return npColumnCount;
  }, [windowWidth, npColumnCount]);

  // ── Chapters list ──────────────────────────────────────────────────────────
  const chapters = useMemo(() => {
    const seen = new Set(); const list = [];
    events.forEach(e => {
      if (e.chapter && !seen.has(e.chapter)) { seen.add(e.chapter); list.push(e.chapter); }
    });
    return list;
  }, [events]);

  // ── Sorted + filtered visible events ──────────────────────────────────────
  // Breaking news always floats to position 0 regardless of sort.
  const visible = useMemo(() => {
    const breaking = events.filter(e => e.breakingNews);
    const rest     = events.filter(e => !e.breakingNews);
    let arr;
    if (sort === 'photos') {
      arr = [...breaking, ...rest.filter(e => e.photo), ...rest.filter(e => !e.photo)];
    } else {
      arr = [...breaking, ...rest];
    }
    return activeChapter ? arr.filter(e => e.chapter === activeChapter) : arr;
  }, [events, sort, activeChapter]);

  // ── Flat items list: section headers + cards ───────────────────────────────
  // When chapters exist and no filter is active, insert section headers.
  const items = useMemo(() => {
    const hasChapters = chapters.length > 0 && !activeChapter;
    if (!hasChapters) return visible.map(ev => ({ type: 'card', ev }));

    const result = [];
    const seenCh = new Set();
    visible.forEach(ev => {
      const ch = ev.chapter || null;
      const key = ch || '__uncat__';
      if (ch && !seenCh.has(key)) {
        seenCh.add(key);
        result.push({ type: 'header', chapter: ch });
      }
      result.push({ type: 'card', ev });
    });
    return result;
  }, [visible, chapters, activeChapter]);

  // ── Masonry calculation ────────────────────────────────────────────────────
  const doLayout = useCallback(() => {
    if (!containerRef.current) return;
    const w = containerRef.current.offsetWidth;
    if (w === 0) return;
    const { positions: pos, containerHeight } = calculateMasonryLayout(
      items, itemRefs.current, effectiveCols, MASONRY_GAP, w
    );
    setPositions(pos);
    setContainerH(containerHeight);
    if (!layoutDone.current) {
      layoutDone.current = true;
      setTimeout(() => setAnimated(true), 80);
    }
  }, [items, effectiveCols]);

  // Re-layout when items or column count changes
  useEffect(() => {
    layoutDone.current = false;
    setAnimated(false);
    itemRefs.current = itemRefs.current.slice(0, items.length);
    const t = setTimeout(doLayout, 50);
    return () => clearTimeout(t);
  }, [items, effectiveCols, doLayout]);

  // ResizeObserver — debounced 150ms
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver(() => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(doLayout, 150);
    });
    obs.observe(containerRef.current);
    return () => { obs.disconnect(); clearTimeout(debounceRef.current); };
  }, [doLayout]);

  // Window resize (tracks effectiveCols)
  useEffect(() => {
    const h = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', h, { passive: true });
    return () => window.removeEventListener('resize', h);
  }, []);

  // ── PNG Export ─────────────────────────────────────────────────────────────
  const handleExportPNG = useCallback(async () => {
    if (!rootRef.current) return;
    setIsExporting(true);
    setExportMsg('Preparing export…');
    const uiEls = rootRef.current.querySelectorAll('.np-toolbar, .np-controls');
    const prevDisplays = Array.from(uiEls).map(e => e.style.display);
    uiEls.forEach(e => { e.style.display = 'none'; });
    await document.fonts.ready;
    try {
      const canvas = await html2canvas(rootRef.current, {
        scale: 2, useCORS: true, allowTaint: true,
        backgroundColor: npSepia ? '#f4e9d0' : (tertiary || '#ffffff'),
        scrollX: 0, scrollY: -window.scrollY,
        width: rootRef.current.scrollWidth,
        height: rootRef.current.scrollHeight,
      });
      uiEls.forEach((e, i) => { e.style.display = prevDisplays[i]; });
      const date = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `${safeSlug(timeline?.title)}_${date}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    } catch (err) {
      uiEls.forEach((e, i) => { e.style.display = prevDisplays[i]; });
      alert(`PNG export failed: ${err.message}`);
    } finally {
      setIsExporting(false); setExportMsg('');
    }
  }, [timeline, npSepia, tertiary]);

  // ── PDF Export ─────────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async () => {
    if (!rootRef.current) return;
    setIsExporting(true);
    setExportMsg('Rendering…');
    const uiEls = rootRef.current.querySelectorAll('.np-toolbar, .np-controls');
    const prevDisplays = Array.from(uiEls).map(e => e.style.display);
    uiEls.forEach(e => { e.style.display = 'none'; });
    await document.fonts.ready;
    try {
      const canvas = await html2canvas(rootRef.current, {
        scale: 2, useCORS: true, allowTaint: true,
        backgroundColor: npSepia ? '#f4e9d0' : (tertiary || '#ffffff'),
        scrollX: 0, scrollY: -window.scrollY,
        width: rootRef.current.scrollWidth,
        height: rootRef.current.scrollHeight,
      });
      uiEls.forEach((e, i) => { e.style.display = prevDisplays[i]; });

      const pageW = 595.28; const pageH = 841.89;
      const MARGIN = 36; const FOOTER_H = 20; const LINE_H = 13;
      const contentW = pageW - MARGIN * 2;
      const titleText = timeline?.title || 'Newspaper Timeline';
      const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

      pdf.setFontSize(10);
      const descRaw = timeline?.description ? stripHtml(timeline.description).trim() : '';
      const descLines = descRaw ? pdf.splitTextToSize(descRaw, contentW) : [];
      let HEADER = MARGIN + 28;
      if (descLines.length) HEADER += descLines.length * LINE_H + 6;
      HEADER += 12;

      const scale2x = 2;
      const natW = canvas.width / scale2x;
      const natH = canvas.height / scale2x;
      const scaleF = contentW / natW;
      const renderedW = contentW;
      const renderedH = natH * scaleF;
      const contentH = pageH - MARGIN - FOOTER_H;
      const firstPageH = contentH - HEADER;
      const totalPages = 1 + Math.max(0, Math.ceil((renderedH - firstPageH) / contentH));

      const drawFooter = (n) => {
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(160, 160, 160);
        pdf.text('Chronos', MARGIN, pageH - MARGIN / 2);
        pdf.text(`Page ${n} of ${totalPages}`, pageW - MARGIN, pageH - MARGIN / 2, { align: 'right' });
      };

      // Page 1: header + first canvas slice
      {
        let hy = MARGIN + 20;
        pdf.setFont('times', 'bold'); pdf.setFontSize(20); pdf.setTextColor(30, 30, 30);
        pdf.text(titleText, MARGIN, hy); hy += 26;
        if (descLines.length) {
          pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(80, 80, 80);
          pdf.text(descLines, MARGIN, hy); hy += descLines.length * LINE_H + 6;
        }
        pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.setTextColor(140, 140, 140);
        pdf.text(today, pageW - MARGIN, MARGIN + 20, { align: 'right' });
        const sliceH = Math.min(firstPageH / scaleF, natH);
        const slice = document.createElement('canvas');
        slice.width = natW * scale2x; slice.height = sliceH * scale2x;
        slice.getContext('2d').drawImage(canvas, 0, 0, slice.width, slice.height, 0, 0, slice.width, slice.height);
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, HEADER, renderedW, sliceH * scaleF);
        drawFooter(1);
      }

      // Remaining pages
      let done = firstPageH; let pageNum = 2;
      while (done < renderedH) {
        pdf.addPage();
        const srcY = done / scaleF;
        const srcH = Math.min(contentH / scaleF, natH - srcY);
        if (srcH <= 0) break;
        const slice = document.createElement('canvas');
        slice.width = natW * scale2x; slice.height = srcH * scale2x;
        slice.getContext('2d').drawImage(canvas, 0, srcY * scale2x, slice.width, slice.height, 0, 0, slice.width, slice.height);
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, MARGIN, renderedW, srcH * scaleF);
        drawFooter(pageNum);
        done += contentH; pageNum++;
      }

      // Chapter bookmarks
      try {
        chapters.forEach(ch => { pdf.outline.add(null, ch, { pageNumber: 1 }); });
      } catch (_) {}

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`${safeSlug(titleText)}_${date}.pdf`);
    } catch (err) {
      uiEls.forEach((e, i) => { e.style.display = prevDisplays[i]; });
      alert(`PDF export failed: ${err.message}`);
    } finally {
      setIsExporting(false); setExportMsg('');
    }
  }, [timeline, tertiary, npSepia, chapters]);

  // ── Embed ──────────────────────────────────────────────────────────────────
  const isPublic = timeline?.privacy === 'public';
  const embedBaseUrl = timeline?.id
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/${timeline.id}`
    : '';
  const safeTitle = (timeline?.title || 'Timeline').replace(/"/g, '&quot;');
  const wAttr = embedResp ? '100%' : embedWidth;
  const hAttr = embedResp ? '100%' : embedHeight;
  const minWidthNote = npColumnCount === 4 ? '1100px' : npColumnCount === 3 ? '900px' : '600px';
  const embedCode = `<iframe\n  src="${embedBaseUrl}"\n  width="${wAttr}"\n  height="${hAttr}"\n  style="border:none;border-radius:12px;"\n  title="${safeTitle}"\n  allowfullscreen\n></iframe>`;
  const copyEmbed = useCallback(() => {
    navigator.clipboard?.writeText(embedCode).catch(() => {});
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  }, [embedCode]);

  // ── Breaking news event (first one, pinned top) ────────────────────────────
  const breakingEvent = useMemo(() => events.find(e => e.breakingNews) || null, [events]);

  // ── Root class names ───────────────────────────────────────────────────────
  const rootCls = [
    'np-root',
    npSepia       ? 'np-sepia'        : '',
    npScanLines   ? 'np-scan-lines'   : '',
    grayscale     ? 'np-grayscale-on' : '',
  ].filter(Boolean).join(' ');

  // CSS custom properties — sepia overrides theme colors
  const cssVars = {
    '--np-primary':   npSepia ? '#3d2b1f' : primary,
    '--np-secondary': npSepia ? '#6b4c2e' : secondary,
    '--np-tertiary':  npSepia ? '#f4e9d0' : tertiary,
    '--np-accent':    npSepia ? '#8b5e3c' : accent,
  };

  // ── Early return: no events ───────────────────────────────────────────────
  if (events.length === 0) {
    return (
      <div className={rootCls} style={cssVars}>
        <div className="np-empty">No events yet. Add your first event to get started.</div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <div ref={rootRef} className={rootCls} style={cssVars}>

      {/* ── Toolbar (owner-only export + viewer grayscale) ─────────────── */}
      {!isEmbed && timeline && (
        <div className="np-toolbar">
          <button
            className="np-toolbar-btn"
            onClick={() => setGrayscale(g => !g)}
            title="Toggle grayscale photos"
          >
            <Icon name="eye" size={12} />
            {grayscale ? 'Color Photos' : 'Grayscale'}
          </button>
          <button className="np-toolbar-btn" onClick={handleExportPNG} title="Export PNG">
            <Icon name="image" size={12} /> PNG
          </button>
          <button className="np-toolbar-btn" onClick={handleExportPDF} title="Export PDF">
            <Icon name="fileText" size={12} /> PDF
          </button>
          {isPublic && (
            <button className="np-toolbar-btn" onClick={() => setEmbedOpen(true)} title="Get embed code">
              <Icon name="code" size={12} /> Embed
            </button>
          )}
        </div>
      )}

      {/* ── Edition header masthead ────────────────────────────────────── */}
      {npEditionHeader ? (
        <div className="np-edition-header">
          <div className="np-edition-top-border" style={{ background: 'var(--np-primary)' }} />
          <div className="np-edition-title">{timeline?.title || 'The Chronicle'}</div>
          {npTagline && <div className="np-edition-tagline">{npTagline}</div>}
          <div className="np-edition-rule" style={{ background: 'var(--np-primary)' }} />
          <div className="np-edition-meta">
            {npEditionNumber && <><span>{npEditionNumber}</span><span className="np-edition-meta-divider">·</span></>}
            <span>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </span>
            <span className="np-edition-meta-divider">·</span>
            <span>All the news fit to display — {events.length} event{events.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="np-edition-rule-thin" />
        </div>
      ) : (
        /* Simple masthead fallback */
        <div className="np-masthead">
          <div className="np-masthead-rule" />
          <div className="np-masthead-tagline">THE TIMELINE CHRONICLE</div>
          <div className="np-masthead-rule" />
        </div>
      )}

      {/* ── Breaking news ticker (below masthead, embed + viewer visible) ─ */}
      {npEditionHeader && npBreakingTicker && breakingEvent && (
        <div className="np-ticker-wrap">
          <span className="np-ticker-label">BREAKING</span>
          <div className="np-ticker-scroll">
            <span className="np-ticker-content">{breakingEvent.title}</span>
          </div>
        </div>
      )}

      {/* ── Controls row ──────────────────────────────────────────────── */}
      <div className="np-controls">
        <div className="np-sort-group">
          <button
            className={`np-sort-btn${sort === 'chrono' ? ' np-sort-active' : ''}`}
            onClick={() => setSort('chrono')}
          >
            Chronological
          </button>
          <button
            className={`np-sort-btn${sort === 'photos' ? ' np-sort-active' : ''}`}
            onClick={() => setSort('photos')}
          >
            Photos First
          </button>
        </div>

        {chapters.length > 0 && (
          <div className="np-chapter-pills">
            <button
              className={`np-pill${activeChapter === null ? ' np-pill-active' : ''}`}
              onClick={() => setActiveChapter(null)}
            >
              All
            </button>
            {chapters.map(ch => (
              <button
                key={ch}
                className={`np-pill${activeChapter === ch ? ' np-pill-active' : ''}`}
                onClick={() => setActiveChapter(ch === activeChapter ? null : ch)}
              >
                {ch}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Masonry grid ──────────────────────────────────────────────── */}
      {visible.length === 0 ? (
        <div className="np-empty">No events to display.</div>
      ) : (
        <div
          ref={containerRef}
          className="np-masonry"
          style={{ height: containerH > 0 ? containerH : 'auto' }}
        >
          {items.map((item, i) => {
            const pos = positions[i];
            const posStyle = pos
              ? { position: 'absolute', top: pos.top, left: pos.left, width: pos.width }
              : { position: 'absolute', top: 0, left: 0, width: '100%', visibility: 'hidden' };

            // ── Section header ──────────────────────────────────────────
            if (item.type === 'header') {
              return (
                <div
                  key={`hdr-${item.chapter}`}
                  ref={el => { itemRefs.current[i] = el; }}
                  className="np-section-header"
                  style={{ ...posStyle, boxSizing: 'border-box' }}
                >
                  <span className="np-section-title">{item.chapter}</span>
                  <div className="np-section-rule" />
                </div>
              );
            }

            // ── Card ────────────────────────────────────────────────────
            const ev = item.ev;
            const isFeatured = !!(ev.featured || ev.breakingNews);
            const photo = ev.photo;
            const desc  = stripHtml(ev.description || '');
            const pullQ = npPullQuotes ? extractPullQuote(ev) : null;
            const isWide = isFeatured && effectiveCols >= 2;

            const onImgLoad = () => {
              clearTimeout(debounceRef.current);
              debounceRef.current = setTimeout(doLayout, 100);
            };

            return (
              <article
                key={ev.id}
                ref={el => { itemRefs.current[i] = el; }}
                className={[
                  'np-card',
                  isFeatured  ? 'np-card-featured'  : '',
                  animated    ? 'np-card-animated'   : '',
                ].filter(Boolean).join(' ')}
                style={{
                  ...posStyle,
                  boxSizing: 'border-box',
                  '--np-hover-shadow': `0 4px 24px ${accent}40`,
                }}
                onClick={() => onEventClick?.(ev)}
                tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEventClick?.(ev); } }}
                role="button"
                aria-label={`View event: ${ev.title}`}
              >
                {/* Breaking news banner */}
                {ev.breakingNews && (
                  <div className="np-breaking-banner">● BREAKING NEWS</div>
                )}

                {/* Wide featured: side-by-side photo + text */}
                {isWide && photo ? (
                  <div className="np-card-featured-inner">
                    <div className="np-card-img-wrap">
                      <img src={photo} alt={ev.title} className="np-card-img" loading="lazy" onLoad={onImgLoad} />
                    </div>
                    <div className="np-card-body">
                      {ev.chapter && <div className="np-card-section">{ev.chapter}</div>}
                      <h2 className="np-card-headline">{ev.title}</h2>
                      <div className="np-card-dateline">
                        {formatDate(ev.startDate)}
                        {ev.endDate && ev.endDate !== ev.startDate ? ` – ${formatDate(ev.endDate)}` : ''}
                      </div>
                      {pullQ && (
                        <div className="np-pull-quote">
                          <span className="np-pull-quote-mark">"</span>
                          <p className="np-pull-quote-text">{pullQ}</p>
                        </div>
                      )}
                      {desc && (
                        <p className="np-card-excerpt">
                          {desc.slice(0, 300)}{desc.length > 300 ? '…' : ''}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Standard card (photo stacked on top if present) */
                  <>
                    {photo && (
                      <div className="np-card-img-wrap">
                        <img src={photo} alt={ev.title} className="np-card-img" loading="lazy" onLoad={onImgLoad} />
                      </div>
                    )}
                    <div className="np-card-body">
                      {ev.chapter && <div className="np-card-section">{ev.chapter}</div>}
                      <h2 className="np-card-headline">{ev.title}</h2>
                      <div className="np-card-dateline">
                        {formatDate(ev.startDate)}
                        {ev.endDate && ev.endDate !== ev.startDate ? ` – ${formatDate(ev.endDate)}` : ''}
                      </div>
                      {pullQ && (
                        <div className="np-pull-quote">
                          <span className="np-pull-quote-mark">"</span>
                          <p className="np-pull-quote-text">{pullQ}</p>
                        </div>
                      )}
                      {desc && (
                        <p className="np-card-excerpt">
                          {isFeatured ? desc.slice(0, 280) : desc.slice(0, 120)}
                          {((isFeatured && desc.length > 280) || (!isFeatured && desc.length > 120)) ? '…' : ''}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </article>
            );
          })}
        </div>
      )}

    </div>

      {/* ── Export overlay — outside rootRef so html2canvas doesn't capture it */}
      {isExporting && (
        <div className="sl-export-overlay" role="status" aria-label={exportMsg}>
          <div className="sl-export-card">
            <div className="export-spinner" aria-hidden="true" />
            <p className="sl-export-msg">{exportMsg || 'Exporting…'}</p>
          </div>
        </div>
      )}

      {/* ── Embed modal ───────────────────────────────────────────────── */}
      <Modal
        open={embedOpen}
        onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }}
        title="Embed Newspaper Timeline"
        width={520}
      >
        <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
          Paste this snippet into any webpage to embed{' '}
          <strong style={{ color: 'var(--text)' }}>{timeline?.title}</strong> as an interactive iframe.
        </p>
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12 }}>
          <div style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>HTML</span>
          </div>
          <pre style={{ padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75, color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {embedCode}
          </pre>
        </div>

        <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>
          Recommended minimum width for {npColumnCount}-column layout: <strong>{minWidthNote}</strong>
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
            <input type="checkbox" checked={embedResp} onChange={e => setEmbedResp(e.target.checked)} style={{ width: 'auto' }} />
            Responsive (100% width)
          </label>
          {!embedResp && (
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ marginBottom: 4 }}>Width</label>
                <input value={embedWidth} onChange={e => setEmbedWidth(e.target.value)} placeholder="e.g. 900 or 100%" style={{ fontSize: 13 }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ marginBottom: 4 }}>Height (px)</label>
                <input value={embedHeight} onChange={e => setEmbedHeight(e.target.value)} placeholder="e.g. 700" style={{ fontSize: 13 }} />
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {[['Small', '600', '500'], ['Medium', '900', '700'], ['Large', '1200', '900']].map(([name, w, h]) => (
              <button
                key={name}
                className="btn-ghost"
                style={{ fontSize: 12, padding: '5px 12px' }}
                onClick={() => { setEmbedWidth(w); setEmbedHeight(h); setEmbedResp(false); }}
              >
                {name} ({w}×{h})
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}>
            Close
          </button>
          <button
            className="btn-primary"
            onClick={copyEmbed}
            style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}
          >
            <Icon name={embedCopied ? 'check' : 'copy'} size={14} />
            {embedCopied ? 'Copied!' : 'Copy Snippet'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default NewspaperTimeline;
